import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Icon } from '../../components/Icon';
import { Wordmark } from '../../components/Logo';
import { useT } from '../../lib/i18n';
import { cn } from '../../lib/cn';

// Registered here as well as on the landing page: /features and /privacy
// mount this header without ever loading the landing page's module.
gsap.registerPlugin(ScrollTrigger);

/**
 * The chrome every public page wears: the header and the two buttons.
 *
 * It lived inside the landing page, so /features, /privacy and /cookies each
 * grew their own two-link version and a visitor who reached one of them lost
 * the way back to anything but the home page. There is one header now, and
 * the links know where they are: an in-page anchor is an anchor on the
 * landing page and a link to the landing page's anchor from anywhere else.
 */

/**
 * The header.
 *
 * At the top of the page the links are out. As the page scrolls they slide
 * to the right, one after another, and fold into the burger; the green
 * button stays. Pressing the burger unfolds them again, and they stay out
 * until the next scroll folds them back. A phone gets the full menu. All of
 * it is CSS transitions on transform and opacity, so it can be interrupted.
 */
export function SiteHeader() {
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  // Every public page is white to its edges, including the overscroll a
  // trackpad reveals above and below it; the application behind is dark.
  useEffect(() => {
    document.documentElement.classList.add('site-page');
    return () => document.documentElement.classList.remove('site-page');
  }, []);

  useEffect(() => {
    const trigger = ScrollTrigger.create({ start: 80, onToggle: (self) => setScrolled(self.isActive) });
    return () => trigger.kill();
  }, []);

  // Coming back to a page at the top must put the header back to its full
  // height, or a route change can leave it folded with no way to unfold.
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
    setScrolled(window.scrollY > 80);
  }, [pathname]);

  // Any further scroll folds an opened menu back in.
  useEffect(() => {
    if (!open) return;
    const fold = () => setOpen(false);
    const id = window.setTimeout(() => ScrollTrigger.addEventListener('scrollStart', fold), 300);
    return () => {
      window.clearTimeout(id);
      ScrollTrigger.removeEventListener('scrollStart', fold);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // On the landing page an anchor is an anchor; anywhere else it has to name
  // the page it belongs to, or it scrolls the current page to nothing.
  const onLanding = useLocation().pathname === '/';
  const section = (hash: string) => (onLanding ? hash : `/${hash}`);
  const links = [
    { href: section('#product'), label: t('landing.navWork') },
    { href: '/features', label: t('landing.navFeatures') },
    { href: section('#pricing'), label: t('landing.navPricing') },
    // Static pages outside the app: a plain link, never routed.
    { href: '/compare', label: t('landing.navCompare'), plain: true },
    { href: section('#story'), label: t('landing.navStudio') },
    { href: '/sign-in', label: t('action.signIn') },
  ];
  const folded = scrolled && !open;

  return (
    <>
      <header
        className={cn(
          // The same column as the page below it: 1200px, centred, 32px gutters.
          'fixed inset-x-0 top-0 z-30 flex items-center justify-between px-4 sm:px-[max(2rem,calc((100vw-1200px)/2+2rem))] transition-[height,background-color] duration-base ease-emphasis',
          scrolled ? 'h-12 bg-canvas/90 backdrop-blur-sm' : 'h-[76px] bg-canvas',
        )}
      >
        <Link to="/" className="flex items-center">
          <Wordmark height={20} />
        </Link>


        <div className="flex items-center gap-5">
          {/* The links, sliding right into the burger as the page scrolls. */}
          <nav className="hidden items-center gap-5 text-[15px] tracking-[-0.01em] md:flex" aria-hidden={folded}>
            {links.map((link, i) => {
              const props = {
                tabIndex: folded ? -1 : 0,
                onClick: () => setOpen(false),
                className: cn(
                  'transition-[opacity,transform] duration-slow ease-emphasis hover:text-muted',
                  folded ? 'pointer-events-none translate-x-10 opacity-0' : 'translate-x-0 opacity-100',
                ),
                // Folding: the link nearest the burger goes first. Unfolding: it comes back last.
                style: { transitionDelay: `${folded ? (links.length - 1 - i) * 40 : i * 40}ms` },
              };
              // An in-page anchor stays an anchor; a route is routed, so the
              // features page does not reload the whole application to open.
              return link.href.startsWith('#') || 'plain' in link ? (
                <a key={link.href} href={link.href} {...props}>
                  {link.label}
                </a>
              ) : (
                <Link key={link.href} to={link.href} {...props}>
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <Go to="/sign-up" small>
            {t('landing.cta')}
          </Go>

          {/* The burger: two lines that are the folded links; a cross while they are out again. */}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? t('landing.closeMenu') : t('landing.menu')}
            className={cn(
              'relative flex h-10 w-10 items-center justify-center transition-opacity duration-base ease-emphasis',
              !scrolled && 'md:pointer-events-none md:opacity-0',
            )}
            tabIndex={scrolled ? 0 : -1}
          >
            <span
              className={cn(
                'absolute h-px w-5 bg-ink transition-transform duration-slow ease-emphasis',
                open ? 'origin-center rotate-45' : 'origin-right',
                !open && (folded ? '-translate-y-[3px] scale-x-100' : '-translate-y-[3px] scale-x-100 md:scale-x-0'),
              )}
              style={{ transitionDelay: folded ? `${links.length * 40}ms` : '0ms' }}
            />
            <span
              className={cn(
                'absolute h-px w-5 bg-ink transition-transform duration-slow ease-emphasis',
                open ? 'origin-center -rotate-45' : 'origin-right',
                !open && (folded ? 'translate-y-[3px] scale-x-100' : 'translate-y-[3px] scale-x-100 md:scale-x-0'),
              )}
              style={{ transitionDelay: folded ? `${links.length * 40 + 60}ms` : '0ms' }}
            />
          </button>
        </div>
      </header>

      {/* The phone menu: the whole room, the links large. */}
      <div
        className={cn(
          'fixed inset-0 z-20 flex flex-col justify-end bg-canvas px-4 pb-12 pt-16 transition-opacity duration-base ease-emphasis md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-hidden={!open}
      >
        <nav className="flex flex-col gap-6">
          {links.map((link, i) => {
            const props = {
              tabIndex: open ? 0 : -1,
              onClick: () => setOpen(false),
              className: cn(
                'text-[40px] font-light leading-none tracking-[-0.02em] transition-[opacity,transform] duration-base ease-emphasis',
                open ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
              ),
              style: { transitionDelay: open ? `${40 + i * 40}ms` : '0ms' },
            };
            return link.href.startsWith('#') || 'plain' in link ? (
              <a key={link.href} href={link.href} {...props}>
                {link.label}
              </a>
            ) : (
              <Link key={link.href} to={link.href} {...props}>
                {link.label}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="h-[76px]" aria-hidden />
    </>
  );
}


/** The green one: it starts something. */
export function Go({ to, small, children }: { to: string; small?: boolean; children: React.ReactNode }) {
  return (
    <Link to={to} className={cn('inline-flex items-center gap-2 bg-go text-on-go transition-[filter,transform] duration-base ease-emphasis hover:brightness-110 active:scale-[0.97]', small ? 'h-8 px-3 text-[14px]' : 'h-11 px-5 text-[16px]')}>
      <Icon name="play" size={small ? 11 : 13} />
      {children}
    </Link>
  );
}

/** The quiet one: a hairline that firms up on hover. */
export function Ghost({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className="inline-flex h-11 items-center border border-line-strong px-5 text-[16px] tracking-[-0.01em] transition-[border-color,transform] duration-base ease-emphasis hover:border-ink active:scale-[0.97]">
      {children}
    </a>
  );
}

